import { Button } from '@crowdfpl/ui-web/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@crowdfpl/ui-web/dialog';
import { Input } from '@crowdfpl/ui-web/input';
import { Label } from '@crowdfpl/ui-web/label';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { trackUmamiEvent } from '@/helpers/analytics';
import { dialogContentResponsive } from '@/helpers/dialog-responsive';
import { getLeagueApiUserToastMessage, getLeagueJoinByCodeInlineError } from '@/helpers/league-api-user-messages';
import { cn } from '@/helpers/utils';
import { useJoinByCode, useLeagueByCode } from '@/hooks/data/leagues';
import { notify } from '@/services/notifications';
import { INVITE_CODE_REGEX } from '@/types/leagues';

const ALLOWED_INVITE_CHARS = new Set('ABCDEFGHJKLMNPQRSTUVWXYZ23456789'.split(''));

export type JoinByCodeModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

function normalizeCode(raw: string): string {
  return raw
    .toUpperCase()
    .split('')
    .filter(c => ALLOWED_INVITE_CHARS.has(c))
    .join('')
    .slice(0, 6);
}

export function JoinByCodeModal({ open, onOpenChange }: JoinByCodeModalProps) {
  const navigate = useNavigate();
  const joinByCode = useJoinByCode();
  const codeInputRef = useRef<HTMLInputElement>(null);
  const [code, setCode] = useState('');
  const [inlineError, setInlineError] = useState<string | null>(null);

  const canSubmit = INVITE_CODE_REGEX.test(code);

  const preview = useLeagueByCode(code, { enabled: open && code.length === 6 });

  useEffect(() => {
    if (!open) {
      setCode('');
      setInlineError(null);
      return;
    }
    const id = window.setTimeout(() => codeInputRef.current?.focus(), 0);
    return () => window.clearTimeout(id);
  }, [open]);

  const handleSubmit = async (): Promise<void> => {
    setInlineError(null);
    try {
      const data = await joinByCode.mutateAsync({ code });
      if (!data.already_member) {
        trackUmamiEvent('league_joined', { leagueId: data.league.id, via: 'code' });
      }
      onOpenChange(false);
      navigate(`/leagues/${data.league.id}`);
    } catch (err) {
      const inline = getLeagueJoinByCodeInlineError(err);
      if (inline) {
        setInlineError(inline);
        return;
      }
      notify.error(getLeagueApiUserToastMessage(err));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={cn(dialogContentResponsive, 'sm:max-w-md')}>
        <DialogHeader>
          <DialogTitle>Join with code</DialogTitle>
          <DialogDescription>Paste the invite code your friend shared.</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-2">
            <Label htmlFor="invite-code-input">Invite code</Label>
            <Input
              ref={codeInputRef}
              id="invite-code-input"
              value={code}
              onChange={e => setCode(normalizeCode(e.target.value))}
              maxLength={6}
              autoCapitalize="characters"
              autoComplete="off"
              spellCheck={false}
              aria-invalid={inlineError !== null}
              aria-describedby={inlineError ? 'invite-code-error' : undefined}
              className="text-center font-mono text-lg tracking-widest"
            />
            {inlineError ? (
              <p id="invite-code-error" role="alert" className="text-sm text-destructive">
                {inlineError}
              </p>
            ) : null}
          </div>

          {preview.data ? (
            <div className="rounded-md border border-border bg-muted/40 px-3 py-2 text-sm">
              <p className="font-semibold">{preview.data.name}</p>
              <p className="text-xs text-muted-foreground">
                {preview.data.member_count !== undefined
                  ? preview.data.member_count === 1
                    ? '1 member'
                    : `${preview.data.member_count} members`
                  : `${preview.data.type} league`}
              </p>
            </div>
          ) : null}
        </div>

        <DialogFooter>
          <Button type="button" onClick={() => void handleSubmit()} disabled={!canSubmit || joinByCode.isPending}>
            {joinByCode.isPending ? 'Joining…' : 'Join league'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
